<template>
  <ComponentState :fetch-status="getOptionPositionsStatus">
    <template #loading>
      <TableSkeleton :row-number="10" :column-number="8" />
    </template>

    <template #error>
      <Error
        @retry="
          () => queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions)
        "
      />
    </template>

    <NTable
      v-if="optionPositions?.length"
      :data="optionPositions"
      :sort-by="{
        prop: 'value',
        order: 'descending',
      }"
      :highlight-by="{
        prop: 'isin',
        value: activeIsin,
      }"
      :striped="false"
      cell-padding="small"
      :column-splitter="false"
      fixed-header
      hover-alignment="right"
      hover-offset="15%"
      class="border-none rounded-none"
      identifier="isin"
      @click.stop
      @row-click="(row) => updateActiveIsin(row.isin)"
    >
      <template #hoveredElements="{ row }">
        <div class="flex items-center h-full gap-2">
          <NButton
            v-gid="{
              module: 'option',
              screen: 'home',
              section: 'position',
              component: 'buyButton',
            }"
            size="mini"
            variant="fill"
            @click.stop="openOrderDialog(row, ORDER_TYPES.buy)"
          >
            <NText variant="overline" weight="medium">
              {{ t('common.buy') }}
            </NText>
          </NButton>

          <NButton
            v-gid="{
              module: 'option',
              screen: 'home',
              section: 'position',
              component: 'sellButton',
            }"
            size="mini"
            color="red"
            variant="fill"
            @click.stop="openOrderDialog(row, ORDER_TYPES.sell)"
          >
            <NText variant="overline" weight="medium">
              {{ t('common.sell') }}
            </NText>
          </NButton>

          <NegarInfoWithBorderIcon
            v-gid="{
              module: 'option',
              screen: 'home',
              section: 'position',
              component: 'infoButton',
            }"
            size="20"
            variant="fill"
            class="h-full"
            @click.stop="openPositionInfoDialog(row)"
          />

          <template
            v-if="calcSettlementDifferenceDays(row.cashSettlementDate) === 0"
          >
            <NText
              v-if="!row.allowCashSettlementRequest"
              variant="overline"
              weight="medium"
            >
              {{ t('option.settlementTimeExpired') }}
            </NText>

            <template v-else-if="row.cashSettlementRequestQuantity">
              <NButton
                size="mini"
                variant="flat"
                @click="openCashSettlementDialog(row, true)"
              >
                <NText variant="overline" weight="medium">
                  {{ t('option.editSettlement') }}
                </NText>
              </NButton>

              <NButton
                ref="deleteSettlementButtonRef"
                size="mini"
                variant="flat"
                color="red"
                @click="
                  handleOpenDeleteSettlementDialog(row, true, $event.target)
                "
              >
                <NText variant="overline" weight="medium">
                  {{ t('common.cancelRequest') }}
                </NText>
              </NButton>
            </template>

            <NButton
              v-else
              v-gid="{
                module: 'option',
                screen: 'home',
                section: 'position',
                component: 'cashSettlementDateButton',
              }"
              size="mini"
              variant="flat"
              :disabled="
                useOptionPositionDetailMapper(row).contractReturnValue <= 0
              "
              @click="openCashSettlementDialog(row)"
            >
              <NText variant="overline" weight="medium">
                {{ cashSettlementLabel(row) }}
              </NText>
            </NButton>
          </template>

          <template
            v-else-if="
              calcSettlementDifferenceDays(row.physicalSettlementDate) === 0
            "
          >
            <NText
              v-if="!row.allowPhysicalSettlementRequest"
              variant="overline"
              weight="medium"
            >
              {{ t('option.settlementTimeExpired') }}
            </NText>

            <template v-else-if="row.positionSide === POSITIONS_SIDE.buy">
              <template v-if="row.physicalSettlementRequestQuantity">
                <NButton
                  size="mini"
                  variant="flat"
                  @click="openPhysicalSettlementDialog(row, true)"
                >
                  <NText variant="overline" weight="medium">
                    {{ t('option.editSettlement') }}
                  </NText>
                </NButton>

                <NButton
                  ref="deleteSettlementButtonRef"
                  size="mini"
                  variant="flat"
                  color="red"
                  @click="
                    handleOpenDeleteSettlementDialog(row, false, $event.target)
                  "
                >
                  <NText variant="overline" weight="medium">
                    {{ t('common.cancelRequest') }}
                  </NText>
                </NButton>
              </template>

              <NButton
                v-else
                v-gid="{
                  module: 'option',
                  screen: 'home',
                  section: 'position',
                  component: 'physicalSettlementDateButton',
                }"
                size="mini"
                variant="flat"
                @click="openPhysicalSettlementDialog(row)"
              >
                <NText variant="overline" weight="medium">
                  {{ t('option.physicalSettlement') }}
                </NText>
              </NButton>
            </template>
          </template>
        </div>
      </template>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.symbol')"
        prop="instrumentName"
        sortable
        align="text-right"
        class="h-10"
      >
        <span class="inline-flex items-center h-5 gap-1">
          <SymbolTradeStateIcon
            class="me-1"
            :trading-state-code="row.tradingState"
          />
          <NText :key="row.instrumentName" variant="caption2" weight="medium">
            {{ row.instrumentName }}
          </NText>
          <NPopper
            v-if="row.collateralQuantity > 0"
            hover
            offset-y="5"
            placement="top"
            append-to-body
          >
            <NButtonIcon
              v-gid="{
                module: 'home',
                screen: 'home',
                section: 'positionTable',
                component: `positionTableTosigh`,
              }"
              size="mini"
              color="gray"
            >
              <NegarLockIcon />
            </NButtonIcon>
            <template #content>
              <div class="px-2 py-1 rounded-md bg-neutral-quaternary">
                <NText
                  class="text-neutral-quaternary"
                  variant="caption2"
                  weight="medium"
                >
                  {{
                    t('option.collateralQuantity', {
                      collateralQuantity: row.collateralQuantity,
                    })
                  }}
                </NText>
              </div>
            </template>
          </NPopper>
        </span>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.openPositionsCount')"
        prop="quantity"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.quantity">
          <NText
            :key="row.quantity"
            variant="caption2"
            weight="medium"
            class="rounded-md px-2 py-0.5"
            :class="
              isBuyOrder(row)
                ? 'bg-brand-secondary text-brand-primary'
                : 'bg-critical-secondary text-critical-primary'
            "
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.net_average')"
        prop="netAveragePrice"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.netAveragePrice" round>
          <NText
            :key="row.netAveragePrice"
            dir="ltr"
            variant="caption2"
            weight="medium"
          >
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('modules.portfolio.current_price')"
        prop="lastTradePrice"
        sortable
        align="text-right"
      >
        <div class="flex items-center">
          <NumberFormatter
            #="{ number }"
            :value="row.referencePriceVariationPercentage / 100"
            maximum-fraction-digits="2"
            number-style="percent"
            sign-display="exceptZero"
          >
            <NText
              :key="row.referencePriceVariationPercentage"
              dir="ltr"
              variant="caption2"
              weight="medium"
              class="ms-1"
              :class="{
                'text-brand-primary': row.referencePriceVariationPercentage > 0,
                'text-critical-primary':
                  row.referencePriceVariationPercentage < 0,
              }"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter #="{ number }" :value="row.lastTradePrice">
            <ChangePriceEffect :price="row.lastTradePrice">
              <NText
                :key="row.lastTradePrice"
                dir="ltr"
                variant="caption2"
                weight="medium"
              >
                {{ number }}
              </NText>
            </ChangePriceEffect>
          </NumberFormatter>
        </div>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('misc.value')"
        prop="value"
        sortable
        align="text-right"
      >
        <NumberFormatter #="{ number }" :value="row.value" round="down">
          <NText :key="row.value" dir="ltr" variant="caption2" weight="medium">
            {{ number }}
          </NText>
        </NumberFormatter>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="`${t('modules.portfolio.profit')}/${t(
          'modules.portfolio.loss'
        )}`"
        prop="returnValue"
        sortable
        align="text-right"
      >
        <div :key="row.returnValuePercentage" class="flex items-center">
          <NumberFormatter
            #="{ number }"
            :value="row.returnValuePercentage / 100"
            number-style="percent"
            maximum-fraction-digits="2"
            sign-display="always"
          >
            <NText
              class="align-middle"
              dir="ltr"
              :class="{
                'text-brand-primary': row.returnValuePercentage > 0,
                'text-critical-primary': row.returnValuePercentage < 0,
              }"
              variant="caption2"
              weight="medium"
            >
              ({{ number }})
            </NText>
          </NumberFormatter>

          <NumberFormatter
            #="{ number }"
            :value="row.returnValue"
            sign-display="always"
            round="down"
          >
            <NText
              class="mx-1 align-middle transition-all duration-300"
              :class="{
                'text-brand-primary': row.returnValuePercentage > 0,
                'text-critical-primary': row.returnValuePercentage < 0,
              }"
              variant="caption2"
              weight="medium"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </NTableColumn>

      <NTableColumn
        v-slot="{ row }"
        :label="t('option.settlementTime')"
        prop="cashSettlementDate"
        sortable
        align="text-right"
      >
        <NText
          v-if="calcSettlementDifferenceDays(row.cashSettlementDate) === 0"
          weight="bold"
          variant="caption2"
          class="ms-1"
        >
          {{ t('option.cashSettlementDue') }}
        </NText>

        <NText
          v-else-if="
            calcSettlementDifferenceDays(row.physicalSettlementDate) === 0
          "
          weight="bold"
          variant="caption2"
          class="ms-1"
        >
          {{ t('option.physicalSettlementDue') }}
        </NText>

        <NText
          v-else-if="
            calcSettlementDifferenceDays(row.physicalSettlementDate) > 0
          "
          weight="bold"
          variant="caption2"
          class="ms-1"
        >
          {{ t('option.settlementTimeExpired') }}
        </NText>

        <span v-else class="inline-flex items-center">
          <NumberFormatter
            #="{ number }"
            :value="
              Math.abs(calcSettlementDifferenceDays(row.physicalSettlementDate))
            "
          >
            <NText weight="bold" variant="caption2">
              {{ number }}
            </NText>
          </NumberFormatter>

          <NText weight="bold" variant="caption2" class="ms-1">
            {{ t('misc.day') }}
          </NText>
        </span>
      </NTableColumn>
    </NTable>

    <EmptyList
      v-else
      :title="t('option.positionTableEmptyListTitle')"
      :body="t('option.positionTableEmptyListDescription')"
      title-class="mt-3 text-center"
      wrapper-class="flex flex-col items-center justify-center w-full"
      class="flex flex-col items-center justify-center h-full my-3 rounded-md"
    >
      <template #image>
        <Image src="empty_box" alt="not_found" class="w-28" />
      </template>

      <NButton
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'portfolioTable',
          component: 'brokerChangeButton',
        }"
        variant="flat"
        color="green"
        class="px-6"
        @click="() => push('/option-history')"
      >
        <NText weight="bold" variant="caption1">
          {{ t('option.visitOptionTradingHistory') }}
        </NText>
      </NButton>
    </EmptyList>
  </ComponentState>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  OPTION_QUERY_KEYS,
  PositionItemWithPrices,
  POSITIONS_SIDE,
  useLiveOptionPositionsQuery,
} from '@core/option';
import { useQueryClient } from '@libs/use';
import { ORDER_SIDES, ORDER_TYPES, useGetServerClock } from '@core/order';
import { useI18n } from 'vue-i18n';
import { getTimeDifference } from '@libs/utils';
import { NegarInfoWithBorderIcon, NegarLockIcon } from '@negar/icons';
import {
  useOptionPositionDetailMapper,
  useDeleteCashSettlementQuery,
  useDeletePhysicalSettlementQuery,
} from '@core/option';
import { NButtonIcon, useDialog, useToast } from '@negar/core';
import { sleep } from '@libs/utils';

import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';
import { PositionTableTabToggle, useOptionDialogs } from '@/modules/option';

import ComponentState from '@/components/ComponentState.vue';
import Error from '@/components/Error.vue';
import TableSkeleton from '@/components/TableSkeleton.vue';
import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import EmptyList from '@/components/EmptyList.vue';
import Image from '@/components/Image.vue';

const { t } = useI18n();
const { push } = useRouter();
const queryClient = useQueryClient();
const toast = useToast();
const { remove: removeDialog } = useDialog();
const { activeIsin, updateActiveIsin } = useActiveSymbolIsin();
const { openNewOrderFormDialog } = useOrderDialogs();
const {
  openSellCashSettlementDialog,
  openBuyCashSettlementDialog,
  openDeleteSettlementDialog,
  openPositionInfoDialog,
  openBuyPhysicalSettlementDialog,
} = useOptionDialogs();

const { getOptionPositionsStatus, optionPositions } =
  useLiveOptionPositionsQuery();

const { time } = useGetServerClock();

const isBuyOrder = (position: PositionItemWithPrices) =>
  position.positionSide === ORDER_SIDES.buy;

const cashSettlementLabel = (row: PositionItemWithPrices) => {
  const { contractReturnValue } = useOptionPositionDetailMapper(row);

  if (contractReturnValue > 0) {
    return t('option.cashSettlement');
  } else if (contractReturnValue < 0) {
    return t('option.contractOutOfTheMoney');
  } else {
    return t('option.contractAtTheMoney');
  }
};

const { deleteCashSettlement, isLoadingDeleteCashSettlement } =
  useDeleteCashSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.delete', {
          settlementType: t('option.settlementRequest.cash'),
        }),
        duration: 3000,
      });

      removeDialog();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const { deletePhysicalSettlement, isLoadingDeletePhysicalSettlement } =
  useDeletePhysicalSettlementQuery({
    async onSuccess() {
      toast.neutral({
        message: t('option.settlementMessage.delete', {
          settlementType: t('option.settlementRequest.physical'),
        }),
        duration: 3000,
      });

      removeDialog();

      await sleep(3000);

      queryClient.invalidateQueries(OPTION_QUERY_KEYS.positions);
    },
    async onReject(error) {
      const message = error.response?.data[0].message;

      if (message) {
        toast.negative({
          message,
          duration: 3000,
        });
      } else {
        toast.negative({
          message: t('option.settlementMessage.error'),
          duration: 3000,
        });
      }
    },
  });

const openOrderDialog = (row: PositionItemWithPrices, type: 'buy' | 'sell') => {
  openNewOrderFormDialog({
    type,
    symbol: {
      isin: row.isin,
      instrumentName: row.instrumentName,
      instrumentTypeCode: row.instrumentTypeCode,
    },
  });
};

const openCashSettlementDialog = (
  row: PositionItemWithPrices,
  isEditMode = false
) => {
  if (row.positionSide === POSITIONS_SIDE.buy) {
    return openBuyCashSettlementDialog({
      isEditMode,
      positionSettlementModel: {
        isin: row.isin,
        instrumentName: row.instrumentName,
        cashSettlementRequestQuantity: row.cashSettlementRequestQuantity,
        cashMaximumQuantity: row.cashMaximumQuantity,
        shareInProportion: row.shareInProportion,
        quantity: row.quantity,
      },
    });
  }

  openSellCashSettlementDialog({
    isEditMode,
    positionSettlementModel: {
      isin: row.isin,
      instrumentName: row.instrumentName,
      cashSettlementRequestQuantity: row.cashSettlementRequestQuantity,
      strikePrice: row.strikePrice,
      contractSize: row.contractSize,
      cashSettlementFee: row.cashSettlementFee,
      underlyingAsseClosingPrice: row.underlyingAsseClosingPrice,
      cashMaximumQuantity: row.cashMaximumQuantity,
      shareInProportion: row.shareInProportion,
      quantity: row.quantity,
    },
  });
};

const openPhysicalSettlementDialog = (
  row: PositionItemWithPrices,
  isEditMode = false
) => {
  return openBuyPhysicalSettlementDialog({
    isEditMode,
    positionPhysicalSettlementModel: row,
  });
};

const handleOpenDeleteSettlementDialog = (
  row: PositionItemWithPrices,
  isInCashSettlementDay,
  deleteSettlementButtonRef: HTMLElement
) => {
  openDeleteSettlementDialog({
    position: row,
    handleDelete: isInCashSettlementDay
      ? deleteCashSettlement
      : deletePhysicalSettlement,
    referenceElementRef: deleteSettlementButtonRef,
    isInCashSettlementDay: isInCashSettlementDay,
    loading: computed(
      () =>
        isLoadingDeletePhysicalSettlement.value ||
        isLoadingDeleteCashSettlement.value
    ),
  });
};

const calcSettlementDifferenceDays = (date: string) =>
  getTimeDifference({
    startTime: new Date(date as string).getTime(),
    endTime: new Date(time.value).getTime(),
  }).differenceInDays;
</script>
